/**
 * ScreenRecorder Component
 *
 * UI for screen recording with preview, controls, and upload.
 */

import { useState, useRef, useEffect } from "react";
import { useScreenRecorder } from "@/hooks/use-screen-recorder";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  MonitorPlay,
  Play,
  Pause,
  Square,
  X,
  Check,
  Loader2,
  AlertTriangle,
  Video,
} from "lucide-react";

interface ScreenRecorderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecordingComplete: (blob: Blob) => void;
  maxDuration?: number; // in seconds
}

export function ScreenRecorder({
  open,
  onOpenChange,
  onRecordingComplete,
  maxDuration = 300, // 5 minutes default
}: ScreenRecorderProps) {
  const previewRef = useRef<HTMLVideoElement>(null);

  const {
    isRecording,
    isPaused,
    recordingTime,
    previewStream,
    isSupported,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  } = useScreenRecorder();

  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Connect preview stream to video element
  useEffect(() => {
    if (previewRef.current && previewStream) {
      previewRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  // Auto-stop at max duration
  useEffect(() => {
    if (isRecording && recordingTime >= maxDuration) {
      handleStop();
    }
  }, [isRecording, recordingTime, maxDuration]);

  // Handle start
  const handleStart = async () => {
    setRecordedBlob(null);
    await startRecording();
  };

  // Handle stop
  const handleStop = async () => {
    const blob = await stopRecording();
    if (blob) {
      setRecordedBlob(blob);
    }
  };

  // Handle confirm (use the recording)
  const handleConfirm = () => {
    if (recordedBlob) {
      onRecordingComplete(recordedBlob);
      setRecordedBlob(null);
      onOpenChange(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isRecording || recordedBlob) {
      setShowConfirmCancel(true);
    } else {
      onOpenChange(false);
    }
  };

  // Confirm cancel
  const confirmCancel = () => {
    if (isRecording) {
      cancelRecording();
    }
    setRecordedBlob(null);
    setShowConfirmCancel(false);
    onOpenChange(false);
  };

  // Handle dialog close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && (isRecording || recordedBlob)) {
      setShowConfirmCancel(true);
    } else {
      onOpenChange(newOpen);
    }
  };

  // Remaining time
  const remainingTime = maxDuration - recordingTime;
  const isNearLimit = remainingTime <= 30;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MonitorPlay className="h-5 w-5" />
              Screen Recording
            </DialogTitle>
            <DialogDescription>
              Record your screen to provide detailed form feedback
            </DialogDescription>
          </DialogHeader>

          {/* Not Supported Warning */}
          {!isSupported && (
            <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div>
                <p className="font-medium text-destructive">
                  Screen recording is not supported
                </p>
                <p className="text-sm text-muted-foreground">
                  Please use Chrome, Firefox, or Edge for screen recording.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Preview Area */}
          <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
            {previewStream ? (
              <video
                ref={previewRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain"
              />
            ) : recordedBlob ? (
              <video
                src={URL.createObjectURL(recordedBlob)}
                controls
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                <Video className="h-12 w-12 mb-3" />
                <p>Click "Start Recording" to begin</p>
              </div>
            )}

            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <div
                  className={cn(
                    "h-3 w-3 rounded-full animate-pulse",
                    isPaused ? "bg-warning" : "bg-destructive"
                  )}
                />
                <Badge variant="secondary" className="bg-foreground/60 text-background">
                  {isPaused ? "PAUSED" : "RECORDING"}
                </Badge>
              </div>
            )}

            {/* Timer */}
            {(isRecording || recordedBlob) && (
              <div className="absolute top-4 right-4">
                <Badge
                  variant="secondary"
                  className={cn(
                    "bg-foreground/60 text-background font-mono",
                    isNearLimit && "bg-destructive"
                  )}
                >
                  {formatTime(recordingTime)}
                  {isRecording && ` / ${formatTime(maxDuration)}`}
                </Badge>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            {!isRecording && !recordedBlob && (
              <Button
                onClick={handleStart}
                disabled={!isSupported}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                Start Recording
              </Button>
            )}

            {isRecording && (
              <>
                {isPaused ? (
                  <Button onClick={resumeRecording} variant="outline" className="gap-2">
                    <Play className="h-4 w-4" />
                    Resume
                  </Button>
                ) : (
                  <Button onClick={pauseRecording} variant="outline" className="gap-2">
                    <Pause className="h-4 w-4" />
                    Pause
                  </Button>
                )}
                <Button onClick={handleStop} variant="destructive" className="gap-2">
                  <Square className="h-4 w-4" />
                  Stop
                </Button>
              </>
            )}

            {recordedBlob && !isRecording && (
              <>
                <Button onClick={handleStart} variant="outline" className="gap-2">
                  <Play className="h-4 w-4" />
                  Re-record
                </Button>
                <Button onClick={handleConfirm} className="gap-2">
                  <Check className="h-4 w-4" />
                  Use This Recording
                </Button>
              </>
            )}
          </div>

          {/* Info */}
          {isSupported && !isRecording && !recordedBlob && (
            <p className="text-xs text-muted-foreground text-center">
              You'll be asked to select a screen, window, or browser tab to record.
              Maximum recording time: {Math.floor(maxDuration / 60)} minutes.
            </p>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={showConfirmCancel} onOpenChange={setShowConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Recording?</AlertDialogTitle>
            <AlertDialogDescription>
              {isRecording
                ? "Your screen recording is still in progress. Are you sure you want to cancel?"
                : "Are you sure you want to discard this recording?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Recording</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmCancel}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ScreenRecorder;
